import { ApiClient } from "./ApiClient";

export interface ContractFileItem {
  /** 已存库的附件携带 id；新上传的可不传 */
  id?: number;
  fileName: string;
  filePath: string;
  fileSize?: number;
}

export interface ContractListItem {
  id: number;
  storeName: string;
  trafficAppId: number;
  trafficAppCode: string;
  trafficAppName: string;
  contractType: "ELECTRONIC" | "PAPER";
  /** 主文件名（files[0]，兼容列表展示） */
  fileName: string;
  filePath: string;
  fileSize: number;
  /** 该合同的所有附件（含主文件） */
  files: ContractFileItem[];
  startDate: string;
  endDate: string;
  status: 1 | 0;
  dateStatus: "ACTIVE" | "EXPIRING" | "EXPIRED" | "INVALID";
  remainingDays: number;
  remark?: string;
}

export interface ContractStat {
  total: number;
  active: number;
  expiring: number;
  expired: number;
}

export interface ContractQuery {
  status?: "ACTIVE" | "EXPIRING" | "EXPIRED";
  trafficAppId?: number;
  bizStatus?: 1 | 0;
  keyword?: string;
  page?: number;
  pageSize?: number;
}

export interface ContractCreateReq {
  storeName: string;
  trafficAppId: number;
  contractType: "ELECTRONIC" | "PAPER";
  /** 合同附件（至少 1 个） */
  files: ContractFileItem[];
  startDate: string;
  endDate: string;
  status?: 1 | 0;
  remark?: string;
}

export interface ContractUpdateReq extends ContractCreateReq {
  id: number;
  status: 1 | 0;
}

export interface UploadedFile {
  fileName: string;
  filePath: string;
  fileSize: number;
}

export interface ContractChannelOption {
  trafficAppId: number;
  trafficAppCode?: string;
  trafficAppName?: string;
}

const api = ApiClient.getDefault();

export const ContractApi = {
  list: (q: ContractQuery) =>
    api.request<{ records: ContractListItem[]; total: number }>(
      "/contract/list",
      "GET",
      q
    ),
  stat: () => api.request<ContractStat>("/contract/stat", "GET"),
  detail: (id: number) =>
    api.request<ContractListItem>("/contract/detail", "GET", { id }),
  /** 懒加载：返回临时签名下载 URL（点击下载/查看时调用） */
  getDownloadUrl: (id: number) =>
    api.request<string>("/contract/downloadUrl", "GET", { id }),
  /** 指定附件（contract_file.id）的临时签名下载 URL */
  getDownloadFileUrl: (fileId: number) =>
    api.request<string>("/contract/downloadFileUrl", "GET", { fileId }),
  /** 渠道下拉：仅返回已有合同的渠道 */
  channels: () =>
    api.request<ContractChannelOption[]>("/contract/channels", "GET"),
  create: (req: ContractCreateReq) =>
    api.request<ContractListItem>("/contract/create", "POST", undefined, req),
  update: (req: ContractUpdateReq) =>
    api.request<boolean>("/contract/update", "POST", undefined, req),
  remove: (id: number) =>
    api.request<boolean>("/contract/delete", "POST", { id }),
  upload: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.request<UploadedFile>("/contract/upload", "POST", undefined, fd);
  },
  /** 导出 Excel：通过 ApiClient 带 X-Auth-Token 拉取 blob，避免 window.open 丢失 token 导致 401 */
  exportExcel: async (q: ContractQuery) => {
    const params: Record<string, string> = {};
    Object.entries(q).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params[k] = String(v);
    });
    const url = await api.downloadBlobUrl("/contract/export", params);
    const a = document.createElement("a");
    a.href = url;
    a.download = "合同列表.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
